import React, { useEffect, useRef, useState } from 'react';
import { gsap } from '../../utils/gsap';

/**
 * CinematicIntro — 4-Phase Opening Sequence
 *
 * Phase 1: Black screen. Technical identifier appears character-by-character.
 * Phase 2: A thin horizontal rule draws across. Identity stabilizes.
 * Phase 3: Main title emerges from beneath a clip-path reveal.
 * Phase 4: "SCROLL" prompt pulses. Overlay dissolves, revealing the hero.
 *
 * Plays once per session (sessionStorage flag).
 */
const CinematicIntro = ({ onComplete }) => {
  const overlayRef = useRef(null);
  const identifierRef = useRef(null);
  const lineRef = useRef(null);
  const titleRef = useRef(null);
  const scrollPromptRef = useRef(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    // Disable scrolling while splash screen is active
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    // Only play once per session
    if (sessionStorage.getItem('intro_played')) {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      setDone(true);
      if (onComplete) onComplete();
      return;
    }

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        onComplete: () => {
          sessionStorage.setItem('intro_played', '1');
          // Fade out overlay
          gsap.to(overlayRef.current, {
            opacity: 0,
            duration: 0.8,
            ease: 'power2.inOut',
            onComplete: () => {
              document.body.style.overflow = '';
              document.documentElement.style.overflow = '';
              setDone(true);
              if (onComplete) onComplete();
            }
          });
        }
      });

      // Phase 1 — identifier appears (chars already placed, just fade in with stagger)
      tl.set(overlayRef.current, { opacity: 1 })
        .fromTo(
          identifierRef.current.querySelectorAll('.ci-char'),
          { opacity: 0, y: 4 },
          { opacity: 1, y: 0, stagger: 0.028, duration: 0.001, ease: 'none' },
          0.3
        )
        // Phase 2 — line draws
        .fromTo(
          lineRef.current,
          { scaleX: 0, transformOrigin: 'left center' },
          { scaleX: 1, duration: 0.9, ease: 'power3.inOut' },
          1.4
        )
        // Phase 3 — title clip reveal
        .fromTo(
          titleRef.current,
          { clipPath: 'inset(100% 0% 0% 0%)', y: 20 },
          { clipPath: 'inset(0% 0% 0% 0%)', y: 0, duration: 1.1, ease: 'power4.out' },
          2.2
        )
        // Phase 4 — scroll prompt
        .fromTo(
          scrollPromptRef.current,
          { opacity: 0 },
          { opacity: 1, duration: 0.6, ease: 'power2.out' },
          3.4
        )
        // Hold for visitor to feel the intro
        .to({}, { duration: 1.2 }, 3.8);
    });

    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      ctx.revert();
    };
  }, [onComplete]);

  if (done) return null;

  const NAME = 'SHAWAIZ ALI REHMAN';
  const ROLE = 'MOBILE & SOFTWARE ENGINEER';

  const renderChars = (str) =>
    str.split('').map((ch, i) => (
      <span className="ci-char" key={i} style={{ display: 'inline-block', whiteSpace: ch === ' ' ? 'pre' : 'normal' }}>
        {ch === ' ' ? '\u00a0' : ch}
      </span>
    ));

  return (
    <div ref={overlayRef} className="ci-overlay" aria-hidden="true">
      <div className="ci-inner">
        {/* Phase 1 + 2 — identifier block */}
        <div ref={identifierRef} className="ci-identifier font-mono">
          <div className="ci-name">{renderChars(NAME)}</div>
          <div ref={lineRef} className="ci-rule" />
          <div className="ci-role">{renderChars(ROLE)}</div>
        </div>

        {/* Phase 3 — main title */}
        <div ref={titleRef} className="ci-title font-display">
          Building<br />
          <span className="ci-title-accent">Software.</span>
        </div>

        {/* Phase 4 — scroll prompt */}
        <div ref={scrollPromptRef} className="ci-scroll-prompt font-mono">
          <span>SCROLL TO BEGIN</span>
          <div className="ci-scroll-line" />
        </div>
      </div>

      <style>{`
        .ci-overlay {
          position: fixed;
          inset: 0;
          z-index: 9999;
          background: #080a0f;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 1;
        }

        .ci-inner {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 0;
          padding: 2rem;
          width: min(640px, 90vw);
        }

        .ci-identifier {
          font-size: clamp(0.65rem, 1.2vw, 0.8rem);
          letter-spacing: 0.22em;
          color: #d4a359;
          margin-bottom: 1.5rem;
        }

        .ci-name {
          margin-bottom: 0.6rem;
          color: rgba(212, 163, 89, 0.7);
        }

        .ci-rule {
          width: 100%;
          height: 1px;
          background: rgba(212, 163, 89, 0.35);
          margin-bottom: 0.6rem;
          transform-origin: left center;
        }

        .ci-role {
          color: rgba(212, 163, 89, 0.5);
        }

        .ci-title {
          font-size: clamp(3.5rem, 9vw, 7rem);
          font-weight: 800;
          line-height: 1.05;
          color: #f3f4f6;
          letter-spacing: -0.03em;
          margin-top: 1rem;
          margin-bottom: 3rem;
          will-change: clip-path, transform;
        }

        .ci-title-accent {
          color: #d4a359;
        }

        .ci-scroll-prompt {
          font-size: 0.7rem;
          letter-spacing: 0.2em;
          color: rgba(107, 114, 128, 0.8);
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .ci-scroll-line {
          width: 1px;
          height: 36px;
          background: linear-gradient(to bottom, rgba(212, 163, 89, 0.6), transparent);
          animation: ci-pulse 1.8s infinite ease-in-out;
        }

        @keyframes ci-pulse {
          0%, 100% { opacity: 0.3; transform: scaleY(0.6); }
          50% { opacity: 1; transform: scaleY(1); }
        }

        @media (max-width: 768px) {
          .ci-inner {
            padding: 1.5rem;
            width: 92vw;
          }

          .ci-title {
            font-size: clamp(2.2rem, 10vw, 3.8rem);
            margin-top: 0.5rem;
            margin-bottom: 2rem;
          }

          .ci-identifier {
            font-size: 0.72rem;
            margin-bottom: 1rem;
          }
        }
      `}</style>
    </div>
  );
};

export default CinematicIntro;
